import React from "react";
import { motion, useReducedMotion } from "framer-motion";

// Configurable demo contact — replace REACT_APP_DEMO_EMAIL to change. Placeholder default, not a government address.
export const DEMO_EMAIL = process.env.REACT_APP_DEMO_EMAIL || "hello@govflowstate.com";

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Scroll-reveal wrapper. Respects reduced-motion (renders static).
export function Reveal({ children, delay = 0, y = 22, className = "", as = "div", ...rest }) {
  const reduce = useReducedMotion();
  const M = motion[as] || motion.div;
  if (reduce) return <div className={className} {...rest}>{children}</div>;
  return (
    <M
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      {...rest}
    >
      {children}
    </M>
  );
}

export function GovMark({ variant = "light", className = "" }) {
  const wordCls = variant === "light" ? "text-white" : "text-brand-ink";
  return (
    <div className={`flex items-center gap-2.5 ${className}`} data-testid="gov-mark">
      <span className="grid h-9 w-9 place-items-center rounded-lg bg-white/10 ring-1 ring-white/15" style={variant === "dark" ? { background: "#EEF4FA", boxShadow: "inset 0 0 0 1px #DCE9F4" } : {}}>
        <img src="/govflow-mark.png" alt="" className="h-6 w-6 object-contain" />
      </span>
      <span className={`font-display font-bold tracking-tight text-[19px] leading-none ${wordCls}`}>GovFlowState</span>
    </div>
  );
}

// Section eyebrow label
export function Eyebrow({ children, tone = "civic" }) {
  const c = tone === "gold" ? "text-brand-gold" : tone === "light" ? "text-brand-civic-100" : "text-brand-civic";
  return <p className={`font-display text-xs font-bold uppercase tracking-[0.22em] ${c}`}>{children}</p>;
}

// Realistic browser chrome wrapper for product imagery
export function BrowserFrame({ url = "app.govflowstate.com/dashboard", children, className = "", testid }) {
  return (
    <div className={`overflow-hidden rounded-xl border border-brand-line bg-white shadow-[0_30px_80px_-20px_rgba(11,40,66,0.45)] ${className}`} data-testid={testid}>
      <div className="flex items-center gap-2 border-b border-brand-line bg-[#F1F5F9] px-4 py-2.5">
        <span className="h-2.5 w-2.5 rounded-full bg-[#E2574C]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#E9B23C]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#4FB477]" />
        <div className="ml-3 flex-1">
          <div className="mx-auto flex max-w-md items-center gap-2 rounded-md bg-white/80 px-3 py-1 text-[11px] text-slate-400 ring-1 ring-brand-line">
            <span className="text-brand-teal">🔒</span>
            <span className="truncate font-mono">{url}</span>
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}
